import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { TanTable } from '../../components/TanTable';
import { ColumnDef } from '@tanstack/react-table';
import api from '../../services/api';
import { CheckIcon, TrashIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import Notification, { NotificationContainer } from '../../components/Notification';
import { Tooltip } from '../../components/ui/Tooltip';
import { Drawer } from '../../components/ui/Drawer';
import { useDispatchFooter } from '../../contexts/DispatchFooterContext';
import { TextField, SelectField, TextAreaField, Toggle, BoolPill } from '../../components/tablemaint/fields';
import EditHistoryLink from '../../components/tablemaint/EditHistoryLink';

interface JoinDefinition {
  id: number;
  join_key: string;
  base_table: string;
  join_type: string;
  target_table: string;
  target_alias: string;
  join_condition: string;
  depends_on_join_key: string | null;
  priority: number;
  is_base: boolean;
  /** One-to-many with the base row: negative operators and blank checks on this
   *  join's fields are asked as a correlated NOT EXISTS / EXISTS (migration 508). */
  is_collection: boolean;
  description: string | null;
}

const JOIN_TYPES = ['LEFT', 'INNER', 'RIGHT', 'FULL'];

const EMPTY_FORM: Partial<JoinDefinition> = {
  join_key: '', base_table: 'orderscurrent', join_type: 'LEFT', target_table: '',
  target_alias: '', join_condition: '', depends_on_join_key: null, priority: 100,
  is_base: false, is_collection: false, description: '',
};

const JoinManager: React.FC = () => {
  const { setDispatchButton } = useDispatchFooter();
  const [data, setData] = useState<JoinDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorNotification, setErrorNotification] = useState<{ title: string; message: string } | null>(null);
  const [successNotification, setSuccessNotification] = useState<{ title: string; message: string } | null>(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<Partial<JoinDefinition>>(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [drawerError, setDrawerError] = useState<string | null>(null);
  const [isRebuilding, setIsRebuilding] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/join-definitions');
      setData(res.data.data || []);
    } catch (error) {
      console.error('Error loading join definitions:', error);
      setErrorNotification({ title: 'Failed to Load', message: 'Could not load join definitions from the server.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const setField = (key: keyof JoinDefinition, value: any) => setForm((prev) => ({ ...prev, [key]: value }));

  const openAdd = useCallback(() => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setDrawerError(null);
    setDrawerOpen(true);
  }, []);

  const openEdit = useCallback((row: JoinDefinition) => {
    setEditingId(row.id);
    setForm({ ...row });
    setDrawerError(null);
    setDrawerOpen(true);
  }, []);

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setDrawerError(null);
  };

  const handleSave = useCallback(async () => {
    if (!form.join_key?.trim() || !form.base_table?.trim() || !form.target_table?.trim()
        || !form.target_alias?.trim() || !form.join_condition?.trim()) {
      setDrawerError('Join key, base table, target table, target alias, and join condition are required.');
      return;
    }
    setIsSaving(true);
    setDrawerError(null);
    try {
      if (editingId != null) {
        await api.put(`/join-definitions/${editingId}`, form);
        setSuccessNotification({ title: 'Updated', message: `Join '${form.join_key}' updated.` });
      } else {
        await api.post('/join-definitions', form);
        setSuccessNotification({ title: 'Created', message: `Join '${form.join_key}' created.` });
      }
      closeDrawer();
      await loadData();
    } catch (error: any) {
      console.error('Error saving join definition:', error);
      setDrawerError(error?.response?.data?.detail || error?.message || 'Unknown error occurred');
    } finally {
      setIsSaving(false);
    }
  }, [form, editingId, loadData]);

  const handleDelete = useCallback(async (row: JoinDefinition) => {
    if (!window.confirm(`Delete join definition '${row.join_key}'?`)) return;
    try {
      await api.delete(`/join-definitions/${row.id}`);
      setSuccessNotification({ title: 'Deleted', message: `Join '${row.join_key}' deleted.` });
      await loadData();
    } catch (error: any) {
      console.error('Error deleting join definition:', error);
      // The backend blocks deletes of joins still referenced by field defs or other joins.
      setErrorNotification({ title: 'Cannot Delete', message: error?.response?.data?.detail || error?.message || 'Unknown error occurred' });
    }
  }, [loadData]);

  const handleSync = useCallback(async () => {
    // Add-only: the backend runs populate_joins.py in its default mode, which inserts
    // missing joins and never writes over one that already exists.
    if (!window.confirm('Sync join definitions from populate_joins.py? Only joins that are missing get added — existing rows are left untouched.')) return;
    setIsRebuilding(true);
    try {
      const res = await api.post('/join-definitions/rebuild');
      const added = res.data?.added_records ?? 0;
      setSuccessNotification({
        title: 'Sync Complete',
        message: added > 0
          ? `${added} new join definition${added === 1 ? '' : 's'} added (${res.data?.total_records ?? ''} total). Existing rows were left unchanged.`
          : `No new join definitions to add (${res.data?.total_records ?? ''} total). Existing rows were left unchanged.`,
      });
      await loadData();
    } catch (error: any) {
      console.error('Error syncing join definitions:', error);
      setErrorNotification({ title: 'Sync Failed', message: error?.response?.data?.detail || error?.message || 'Unknown error occurred' });
    } finally {
      setIsRebuilding(false);
    }
  }, [loadData]);

  // Sync Joins button lives in the shared dispatch footer (page-level action).
  useEffect(() => {
    setDispatchButton(
      <button
        onClick={handleSync}
        disabled={isRebuilding}
        className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2"
      >
        <ArrowPathIcon className={`w-4 h-4 ${isRebuilding ? 'animate-spin' : ''}`} />
        {isRebuilding ? 'Syncing…' : 'Sync Joins'}
      </button>,
    );
    return () => setDispatchButton(null);
  }, [setDispatchButton, handleSync, isRebuilding]);

  const truncCell = (v: any) =>
    v ? <span title={String(v)} className="block max-w-[260px] truncate text-xs">{String(v)}</span> : '';

  const columns = useMemo<ColumnDef<JoinDefinition>[]>(() => [
    { accessorKey: 'priority', header: 'Pri', size: 60, cell: ({ row }) => <span className="block text-right">{row.original.priority}</span> },
    { accessorKey: 'join_key', header: 'Join Key', size: 150, enableColumnFilter: true, filterFn: 'arrIncludes' },
    { accessorKey: 'join_type', header: 'Type', size: 80, enableColumnFilter: true, filterFn: 'arrIncludes' },
    { accessorKey: 'base_table', header: 'Base Table', size: 130 },
    { accessorKey: 'target_table', header: 'Target Table', size: 130 },
    { accessorKey: 'target_alias', header: 'Alias', size: 80 },
    { accessorKey: 'join_condition', header: 'ON Condition', size: 240, cell: ({ row }) => truncCell(row.original.join_condition) },
    { accessorKey: 'depends_on_join_key', header: 'Depends On', size: 120, cell: ({ row }) => row.original.depends_on_join_key || '' },
    { accessorKey: 'is_base', header: 'Base', size: 70, cell: ({ row }) => <BoolPill value={!!row.original.is_base} /> },
    { accessorKey: 'is_collection', header: 'Collection', size: 95, cell: ({ row }) => <BoolPill value={!!row.original.is_collection} /> },
    { accessorKey: 'description', header: 'Description', size: 180, cell: ({ row }) => row.original.description || '' },
    {
      id: 'actions', header: 'Actions', size: 70, enableSorting: false, enableColumnFilter: false,
      // Row click opens the editor; delete stays here (stopPropagation so it
      // doesn't also open the drawer).
      cell: ({ row }) => (
        <div className="flex gap-2 justify-end">
          <Tooltip content="Delete">
            <button onClick={(e) => { e.stopPropagation(); handleDelete(row.original); }} className="p-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300">
              <TrashIcon className="w-4 h-4" />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ], [handleDelete]);

  // depends_on options = the other join keys (a join can't depend on itself).
  const dependsOptions = useMemo(
    () => [
      { value: '', label: '— None —' },
      ...data.filter((j) => j.join_key !== form.join_key).map((j) => ({ value: j.join_key, label: j.join_key, description: j.description || j.target_table })),
    ],
    [data, form.join_key],
  );

  return (
    <div className="h-full w-full p-10 pb-24">
      <TanTable
        data={data}
        columns={columns}
        loading={loading}
        settingsKey="join-manager-table"
        fieldName="join-manager-table"
        searchPlaceholder="Search join definitions…"
        cardTitle="Join Definitions"
        enableGrouping={false}
        enableColumnFilters={true}
        onEditFilters={openAdd}
        showEditFiltersButton={true}
        editFiltersButtonIcon="plus"
        editFiltersButtonTooltip="Add Join Definition"
        onRowClick={openEdit}
        lockedColumns={[{ id: 'actions', side: 'right' }]}
      />

      <Drawer
        isOpen={drawerOpen}
        onClose={closeDrawer}
        title={editingId != null ? 'Edit Join Definition' : 'Add Join Definition'}
        position="right"
        maxWidth="lg"
        footer={
          <div className="flex items-center justify-end gap-3">
            <button onClick={closeDrawer} disabled={isSaving} className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
              Cancel
            </button>
            <button onClick={handleSave} disabled={isSaving} className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2">
              <CheckIcon className="w-4 h-4" />
              {isSaving ? 'Saving…' : editingId != null ? 'Save Changes' : 'Add Join'}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          {editingId != null && (
            <div className="flex justify-end">
              <EditHistoryLink endpoint={`/join-definitions/${editingId}/history`} label={form.join_key} />
            </div>
          )}
          {drawerError && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
              {drawerError}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextField label="Join Key" required value={form.join_key ?? ''} onChange={(v) => setField('join_key', v)}
              disabled={isSaving || editingId != null} placeholder="e.g., dleg"
            />
            <TextField label="Priority" type="number" value={String(form.priority ?? 100)} onChange={(v) => setField('priority', v === '' ? 0 : parseInt(v, 10))} disabled={isSaving} />
            <SelectField label="Join Type" required clearable={false} value={form.join_type ?? 'LEFT'} onChange={(v) => setField('join_type', v)} options={JOIN_TYPES.map((t) => ({ value: t, label: t }))} disabled={isSaving} />
            <TextField label="Base Table" required value={form.base_table ?? ''} onChange={(v) => setField('base_table', v)} disabled={isSaving} />
            <TextField label="Target Table" required value={form.target_table ?? ''} onChange={(v) => setField('target_table', v)} disabled={isSaving} />
            <TextField label="Target Alias" required value={form.target_alias ?? ''} onChange={(v) => setField('target_alias', v)} disabled={isSaving} />
            <SelectField label="Depends On (Join Key)" value={form.depends_on_join_key ?? ''} onChange={(v) => setField('depends_on_join_key', v || null)} options={dependsOptions} showDescriptions disabled={isSaving} />
            <Toggle label="Is Base FROM Table" checked={!!form.is_base} onChange={(v) => setField('is_base', v)} disabled={isSaving} />
            <Toggle label="One-to-Many (Collection)" checked={!!form.is_collection} onChange={(v) => setField('is_collection', v)} disabled={isSaving} />
            <TextAreaField label="ON Condition (raw SQL)" value={form.join_condition ?? ''} onChange={(v) => setField('join_condition', v)} rows={3} disabled={isSaving} required />
            <TextAreaField label="Description" value={form.description ?? ''} onChange={(v) => setField('description', v || null)} rows={2} disabled={isSaving} />
          </div>
          {editingId != null && (
            <p className="text-xs text-muted">
              Join Key is the identity referenced by field definitions and other joins, so it can't be changed here. Use <code>{'{schema}'}</code> in the ON condition for the schema placeholder.
            </p>
          )}
          <p className="text-xs text-muted">
            Tick <strong>One-to-Many (Collection)</strong> when this join can return more
            than one row per base row. Filters on its fields then ask &ldquo;does NO related
            row match&rdquo; for negative operators and blank checks, instead of &ldquo;does
            some other row differ&rdquo; &mdash; which is what a plain WHERE means on a
            fan-out, and almost never what the user wants. Leave it off for a join pinned to
            a single row (a specific leg number, the main invoice, a lookup).
          </p>
        </div>
      </Drawer>

      {errorNotification && createPortal(
        <NotificationContainer position="top-right">
          <Notification type="error" title={errorNotification.title} message={errorNotification.message} showClose onClose={() => setErrorNotification(null)} autoDismiss={6000} />
        </NotificationContainer>,
        document.body,
      )}
      {successNotification && createPortal(
        <NotificationContainer position="top-right">
          <Notification type="success" title={successNotification.title} message={successNotification.message} showClose onClose={() => setSuccessNotification(null)} autoDismiss={3000} />
        </NotificationContainer>,
        document.body,
      )}
    </div>
  );
};

export default JoinManager;
